import { Pagination, PaginationResponse } from '@ekkogmbh/apisdk';
import { Box, Divider, IconButton, Skeleton, Stack, TableCell, Tooltip, Typography } from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableColumnOptions, MUIDataTableOptions } from 'mui-datatables';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KeyboardArrowLeft } from '@mui/icons-material';
import { RowSelect } from './RowSelect';
import { ColumnActionButtonProps } from './DataTable/ColumnActionButton';
import ActionCell from './DataTable/ActionCell';
import { DataTableHeader } from './DataTable/DataTableHeader';
import Highlighter from 'react-highlight-words';
import { LoadingMask } from './LoadingMask';
import { useSearchBar } from '../Stores/SearchBarProvider';
import useRequest from '../Hooks/useRequest';
import usePagination from '../Providers/PaginationProvider';
import useDataTable, { DataTableData, DataTableRecord } from '../Providers/DataTableProvider';

const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
});

export function dataTableDateTimeFormat(date: Date): string {
  return dateTimeFormatter.format(date);
}

export function dataTableHighlight(text: string, highlightText?: string | string[]): ReactNode {
  if (highlightText === undefined) {
    return text;
  }
  const searchInputs = typeof highlightText === 'string' ? [highlightText] : highlightText;
  return (
    <Box
      component="span"
      sx={{
        '& mark': {
          backgroundColor: 'secondary.main',
          color: 'secondary.contrastText',
          p: 0,
        },
      }}
    >
      <Highlighter autoEscape textToHighlight={text} searchWords={searchInputs} />
    </Box>
  );
}

export interface ColumnDef {
  header: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: (entry: any, searchText?: string) => ReactNode;
  sortField?: string;
  filterField?: string;
  filterInputOptions?: Record<string, string>;
  searchTooltip?: ReactNode;
  /**
   * When true, the column will only be as wide as its content requires
   * instead of stretching to fill available table space.
   */
  fitContent?: boolean;
  /**
   * Assigns a proportional share of the remaining table space to this column.
   * Value is a CSS percentage (e.g. '60%', '25%'). The column will shrink
   * below its natural content width and scroll internally instead of pushing
   * the table wider. Mutually exclusive with `fitContent`.
   *
   * Under the hood this sets `min-width: 0` and `max-width: 0` on the cell,
   * which breaks the default `table-layout: auto` behavior where content
   * determines column width. The percentage `width` then controls how
   * remaining space is distributed among proportional columns.
   *
   * Note: do NOT use `max-width` on the `<td>` to cap a proportional column.
   * In `table-layout: auto`, `max-width` on a cell acts as a floor (minimum),
   * not a ceiling — the algorithm allocates at least that much space when
   * content warrants it. If a cap is needed, apply it on the inner content
   * component (e.g. ScrollableStack maxWidth) instead, accepting that the
   * `<td>` itself may be wider at very wide viewports.
   */
  proportionalContent?: string;
}

interface Props {
  /** CAUTION: wrap API functions in a callback to prevent stale ref issues */
  fetchItems: (pagination: Pagination) => Promise<PaginationResponse<DataTableRecord>>;
  onBack?: () => void;
  title?: string;
  selectableRows?: boolean;
  actions?: ColumnActionButtonProps[];
  /** Number of actions shown as individual buttons before collapsing into a burger menu. Defaults to 2. */
  maxExpandedActions?: number;
  columns: ColumnDef[];
  preFilter?: Record<string, string>;
  defaultSort?: { direction: 'asc' | 'desc'; sortField: string };
}

export const DataTable = ({
  actions,
  maxExpandedActions = 2,
  columns,
  fetchItems,
  onBack,
  title,
  selectableRows,
  preFilter,
  defaultSort,
}: Props): React.JSX.Element => {
  const {
    data,
    setData,
    setTotalItemCount,
    sortBy,
    sortDirection,
    setSortBy,
    setSortDirection,
    committedFilter,
    commitFilter,
    registerSearchHandlers,
    setHasColumnFilterInput,
    setIsSearchActive,
    fieldsFilter,
    setFieldsFilter,
    registerClearColumnFilters,
  } = useDataTable();
  const { page, refresh, setPage, subscribeRefresh, unsubscribeRefresh } = usePagination();
  const { searchText, setSearchText } = useSearchBar();

  // Gate that prevents the initial data fetch from firing until stale
  // search/filter state from a previous page has been flushed.
  // setSearchText('') and setIsStaleFilterCleared(true) are batched by
  // React into one re-render, so by the time the gated effect runs,
  // prunedFieldsFilter has already been recomputed cleanly.
  const [isStaleFilterCleared, setIsStaleFilterCleared] = useState(false);

  /** Used to only trigger a refresh when filter inputs are confirmed at runtime (not mount). */
  const isRefreshPending = useRef<boolean>(false);
  const [isFetching, startFetching] = useRequest<PaginationResponse<DataTableRecord>>();

  // Clear search text and column filters on mount so navigation
  // between pages starts fresh. All three state updates are batched
  // into a single re-render, ensuring prunedFieldsFilter is clean
  // before the isStaleFilterCleared gate opens.
  useEffect(() => {
    setSearchText('');
    setFieldsFilter({});
    setIsStaleFilterCleared(true);
  }, []);

  const setFieldFilter = useCallback(
    (field: string, value: string = '') => {
      setFieldsFilter((prev) => ({ ...prev, [field]: value }));
    },
    [setFieldsFilter],
  );

  /** Init a base filter based on given props */
  const columnFilter = useMemo(
    () =>
      columns.reduce((carry, col): Record<string, string> => {
        if (col.filterField) {
          return { ...carry, [col.filterField]: '' };
        }

        return carry;
      }, {}),
    [columns],
  );
  const combinedFilter = useMemo(
    (): Record<string, string> => ({ ...columnFilter, ...preFilter, ...fieldsFilter }),
    [columnFilter, preFilter, fieldsFilter],
  );

  const prunedFieldsFilter = useMemo(() => {
    const newFilter: Record<string, string> = {};
    // do not use empty fields
    Object.entries(combinedFilter).forEach(([key, value]) => {
      if (value !== '') {
        newFilter[key] = value;
      }
    });

    // add search if given
    if (searchText !== '') {
      newFilter.search = searchText;
    }

    return newFilter;
  }, [searchText, combinedFilter]);

  // Ref to the latest draft filter so commitAndRefresh can read it
  // without being recreated on every keystroke.
  const prunedFieldsFilterRef = useRef(prunedFieldsFilter);
  prunedFieldsFilterRef.current = prunedFieldsFilter;

  // Ref to the latest sort state so fetchData can read it without
  // depending on sort changes (which would cause re-subscription).
  const sortRef = useRef({ sortBy, sortDirection });
  sortRef.current = { sortBy, sortDirection };

  // Ref to the current page so commitAndRefresh can branch on it without
  // having to depend on `page` (which would re-register search handlers
  // on every pagination change).
  const pageRef = useRef(page);
  pageRef.current = page;

  // Snapshots the current draft filter into committedFilter and
  // triggers a data fetch. All search execution paths (column filter
  // submit, search bar Enter/button, clear) go through this function.
  //
  // A new search must always start on page 1 — otherwise the user sees
  // results for page N of the new filter (often empty / out of range).
  // setPage already emits a refresh via PaginationProvider's page-change
  // effect, so we only call refresh() directly when the page was already 1.
  const commitAndRefresh = useCallback(() => {
    commitFilter(prunedFieldsFilterRef.current);
    if (pageRef.current !== 1) {
      setPage(1);
    } else {
      refresh();
    }
  }, [commitFilter, refresh, setPage]);

  /** set provided sort parameters */
  useEffect(() => {
    setSortBy(defaultSort?.sortField);
    setSortDirection(defaultSort?.direction);
  }, [defaultSort]);

  // Trigger a refresh when sort changes (replaces PaginationProvider's
  // former auto-refresh on filtering.sort/filtering.direction).
  const isInitialSort = useRef(true);
  useEffect(() => {
    if (isInitialSort.current) {
      isInitialSort.current = false;
      return;
    }
    refresh();
  }, [sortBy, sortDirection, refresh]);

  // Ref to columns so fetchData can apply JSON basePath transformations
  // without depending on columns (which would cause re-subscription).
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const fetchData = useCallback(
    (pagination: Pagination) => {
      const rawFilter = prunedFieldsFilterRef.current;
      const { sortBy: sort, sortDirection: direction } = sortRef.current;

      // Apply JSON basePath transformations for the API call.
      // fieldsFilter stores raw user-facing text; basePath is only
      // prepended when sending to the backend.
      const filter = { ...rawFilter };
      for (const col of columnsRef.current) {
        const { filterField, filterInputOptions } = col;
        if (
          filterField &&
          filter[filterField] &&
          filterInputOptions?.variant === 'json' &&
          filterInputOptions?.basePath
        ) {
          const raw = filter[filterField];
          const paramSlice = raw.slice(1);
          filter[filterField] =
            '{' + filterInputOptions.basePath + (paramSlice.startsWith('}') ? '' : ',') + paramSlice;
        }
      }

      startFetching(
        () =>
          fetchItems({
            page: pagination.page,
            limit: pagination.limit,
            sort,
            direction,
            filter: Object.keys(filter).length > 0 ? filter : undefined,
          }),
        (result) => {
          const response = result as PaginationResponse<DataTableRecord>;
          const items = (response.items ?? []) as DataTableData[];
          const itemCount = response.totalItemCount ?? 0;

          if (items.length === 0 && itemCount > 0 && page > 1) {
            setPage(1);
          } else {
            setData(items);
            setTotalItemCount(itemCount);
          }
        },
      );
    },
    [fetchItems],
  );

  /** Handle fetch triggering */
  useEffect(() => {
    console.log('[TABLE] refresh subscribed');
    subscribeRefresh(fetchData);
    return () => unsubscribeRefresh(fetchData);
  }, [fetchData]);

  // Initial fetch — fires exactly once when isStaleFilterCleared flips
  // to true on the re-render where searchText and fieldsFilter are
  // already clean.
  useEffect(() => {
    if (!isStaleFilterCleared) {
      return;
    }
    commitAndRefresh();
  }, [isStaleFilterCleared, commitAndRefresh]);

  // Runtime refresh trigger — used by doClearSearch, onClear, and
  // clearColumnFilters. Gated on isStaleFilterCleared so it can't fire
  // before the mount-clear has propagated.
  useEffect(() => {
    if (!isStaleFilterCleared) {
      return;
    }
    if (isRefreshPending.current) {
      isRefreshPending.current = false;
      commitAndRefresh();
    }
  }, [isStaleFilterCleared, fieldsFilter, searchText, commitAndRefresh]);

  // =========================================================
  // Search Handler Registration
  // =========================================================

  const doClearSearch = useCallback(() => {
    setSearchText('');
    isRefreshPending.current = true;
  }, [setSearchText]);

  useEffect(() => {
    registerSearchHandlers(commitAndRefresh, doClearSearch);
  }, [commitAndRefresh, doClearSearch, registerSearchHandlers]);

  // =========================================================
  // Column Filter ↔ Global Search Mutual Exclusivity
  // =========================================================

  // Notify the provider whether any column filter has draft text.
  useEffect(() => {
    const hasInput = Object.values(fieldsFilter).some((v) => v !== '');
    setHasColumnFilterInput(hasInput);
  }, [fieldsFilter, setHasColumnFilterInput]);

  // Sync isSearchActive — only triggers a provider re-render when the
  // boolean actually flips (React bails out on same-value setState).
  useEffect(() => {
    setIsSearchActive(searchText !== '');
  }, [searchText, setIsSearchActive]);

  // Reset mutual-exclusivity state on unmount so navigating away from a
  // table page doesn't leave stale state in the provider.
  useEffect(
    () => () => {
      setHasColumnFilterInput(false);
      setIsSearchActive(false);
    },
    [setHasColumnFilterInput, setIsSearchActive],
  );

  // Ref to committed filter for the clear callback (avoids re-creating
  // the callback on every committed filter change).
  const committedFilterRef = useRef(committedFilter);
  committedFilterRef.current = committedFilter;

  const clearColumnFilters = useCallback(() => {
    setFieldsFilter({});

    // Only refetch if column filters were actually committed.
    // Draft-only clearing doesn't need a round-trip.
    const committed = committedFilterRef.current;
    const hasCommittedColumnFilters = Object.keys(committed).some((k) => k !== 'search');
    if (hasCommittedColumnFilters) {
      isRefreshPending.current = true;
    }
  }, [setFieldsFilter]);

  useEffect(() => {
    registerClearColumnFilters(clearColumnFilters);
  }, [clearColumnFilters, registerClearColumnFilters]);

  // =========================================================
  // Table Columns
  // =========================================================

  const dataTableData: { entry: DataTableRecord; index: number }[] = useMemo(
    () =>
      data.map((value: DataTableRecord, index) => ({
        entry: value,
        index,
      })),
    [data],
  );

  const rowSelectColumn: MUIDataTableColumnDef = useMemo(
    () => ({
      name: 'index',
      options: {
        sort: false,
        filter: false,
        // Prevent table layout from stretching this column beyond its content
        setCellProps: () => ({ style: { width: '1%' } }),
        customHeadRender: (columnMeta) => (
          <TableCell
            key={columnMeta.index}
            scope={'col'}
            style={{
              padding: '4px 8px',
            }}
          >
            <RowSelect />
          </TableCell>
        ),
        customBodyRenderLite: (index: number) => <RowSelect dataTableIndex={Number(index)} />,
      },
    }),
    [],
  );

  const actionColumn = useMemo((): MUIDataTableColumnDef | undefined => {
    if (actions === undefined || actions.length === 0) {
      return undefined;
    }

    return {
      label: ' ',
      name: 'entry',
      options: {
        sort: false,
        filter: false,
        // Prevent table layout from stretching this column beyond its content
        setCellProps: () => ({ style: { width: '1%' } }),
        customHeadRender: (columnMeta) => (
          <TableCell
            key={columnMeta.index}
            scope={'col'}
            style={{
              padding: '4px 8px',
            }}
          >
            <Box sx={{ p: '2px 0px', display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
            </Box>
          </TableCell>
        ),
        customBodyRender: (_, tableMeta) => {
          const { entry } = dataTableData[tableMeta.rowIndex];
          if (entry === undefined) {
            return <Skeleton variant={'circular'} />;
          }
          return <ActionCell actions={actions} entry={entry} maxExpandedActions={maxExpandedActions} />;
        },
      },
    };
  }, [actions, dataTableData, maxExpandedActions]);

  const columnHeader = useCallback(
    (column: ColumnDef, index: number) => {
      const { sortField, filterField } = column;
      const sortFn = sortField
        ? (direction?: 'asc' | 'desc') => {
            setSortBy(sortField);
            setSortDirection(direction);
          }
        : undefined;

      const filterFn = filterField
        ? (value: string) => {
            setFieldFilter(filterField, value);
          }
        : undefined;
      return (
        <DataTableHeader
          key={column.header}
          sortDirection={sortBy === sortField ? (sortDirection as 'asc' | 'desc') : undefined}
          index={index}
          column={column}
          onFilterInput={filterFn}
          onSortBy={sortFn}
          onClear={() => {
            isRefreshPending.current = true;
          }}
          onSearch={() => {
            commitAndRefresh();
          }}
        />
      );
    },
    [sortBy, sortDirection],
  );

  const columnCell = useCallback(
    (column: ColumnDef, entry: unknown, searchInput?: string) => {
      if (isFetching) {
        return <Skeleton />;
      }
      const { body } = column;

      let bodyElement: ReactNode;
      try {
        bodyElement = body(entry, searchInput);
      } catch (e) {
        // The table doesnt always clear its data when columns change
        // This works around these transition issues.
        // And if the render function truly is broken, we should see an out-of-place Skeleton
        // But the page itself should still load
        // I think this is fine
        return <Skeleton />;
      }

      if (typeof bodyElement === 'string' && searchInput) {
        bodyElement = dataTableHighlight(bodyElement, [searchInput]);
      }

      return <Box marginLeft={1}>{bodyElement}</Box>;
    },
    [isFetching],
  );

  const contentColumns: MUIDataTableColumnDef[] = useMemo(() => {
    const highlight = Object.keys(committedFilter).length > 0 ? committedFilter : undefined;

    const cols: MUIDataTableColumnDef[] = columns.map((col: ColumnDef, index: number): MUIDataTableColumnDef => {
      const { filterField } = col;
      const input = (filterField && highlight?.[filterField]) ?? highlight?.search ?? '';

      const options: MUIDataTableColumnOptions = {
        sort: false,
        filter: false,
        customBodyRender: (entry: unknown) => columnCell(col, entry, input),
        customHeadRender: () => columnHeader(col, index),
      };

      // Build cell style from column layout hints.
      //
      // fitContent: Setting width: 1% on a <td> in a table-layout: auto table
      // tells the layout algorithm to allocate minimal space — the cell still
      // grows to fit its content, but won't claim surplus horizontal space.
      //
      // proportionalContent: Setting min-width: 0 + max-width: 0 breaks the
      // default behavior where content determines column width. The cell then
      // accepts whatever width the table algorithm assigns via its percentage
      // `width` value. Content inside must handle its own overflow (e.g. via
      // ScrollableStack with width="100%").
      const cellStyle: React.CSSProperties = {};

      if (col.fitContent) {
        cellStyle.width = '1%';
      }

      if (col.proportionalContent) {
        cellStyle.width = col.proportionalContent;
        cellStyle.minWidth = '0';
        cellStyle.maxWidth = '0';
        cellStyle.overflow = 'hidden';
      }

      if (Object.keys(cellStyle).length > 0) {
        options.setCellProps = () => ({ style: cellStyle });
      }

      return { name: 'entry', options };
    });

    if (actionColumn) {
      cols.push(actionColumn);
    }

    if (selectableRows) {
      cols.unshift(rowSelectColumn);
    }

    return cols;
  }, [columns, committedFilter, columnCell, columnHeader, actionColumn, selectableRows, rowSelectColumn]);

  // =========================================================
  // Table Options
  // =========================================================

  // Footer to prevent MUI Datatables to render its pagination, we we handle
  // internally ourselves.
  const tableFooter = useCallback(() => <></>, []);

  const tableOptions = useMemo((): MUIDataTableOptions => {
    // Disable all MUI DataTable chrome — we handle filtering, sorting,
    // pagination, and search externally via our own providers.
    const options: MUIDataTableOptions = {
      // Wrap the table in a flat Paper. In dark mode, MUI applies an
      // "elevation overlay" (a white tint that lightens Paper as elevation
      // grows, since shadows don't read on dark surfaces). At the default
      // elevation 4, that overlay made the table's surface visibly lighter
      // than its own background.paper, so children styled with
      // background.paper (e.g. filter inputs) read as darker tiles.
      // Elevation 0 disables the overlay so the surface matches the token.
      elevation: 0,
      rowsPerPage: dataTableData.length,
      enableNestedDataAccess: '.',
      serverSide: true,
      filter: false,
      viewColumns: false,
      search: false,
      print: false,
      download: false,
      selectableRows: 'none',
      responsive: 'standard',
      customFooter: tableFooter,
      textLabels: {
        body: {
          noMatch: isFetching ? '. . .' : 'Sorry, no matching records found',
        },
      },
    };

    return options;
  }, [dataTableData.length, isFetching, tableFooter]);

  const table = useMemo(
    () => <MUIDataTable title={''} data={dataTableData} columns={contentColumns} options={tableOptions} />,
    [dataTableData, contentColumns, tableOptions],
  );

  const loadMask = useMemo(
    () => <LoadingMask width={75} height={75} topPercent={dataTableData.length > 10 ? 5 : 50} isLoading={isFetching} />,
    [isFetching, dataTableData.length],
  );

  return (
    <Box component={'div'}>
      <Stack>
        {onBack !== undefined && (
          <Tooltip title="Go back">
            <IconButton
              size="small"
              onClick={onBack}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                zIndex: 42,
              }}
            >
              <KeyboardArrowLeft />
            </IconButton>
          </Tooltip>
        )}
        {title !== undefined && (
          <Typography align={'center'} variant={'overline'} style={{ marginLeft: 8 }}>
            {title}
          </Typography>
        )}
      </Stack>
      {loadMask}
      {table}
    </Box>
  );
};
